import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { BarChart3, CheckCircle2, ChevronDown, ChevronUp, Loader2, RotateCcw, Search, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('authToken') || localStorage.getItem('token');
const api = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/performance${path}`, { headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' }, ...opts });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Request failed (${res.status})`); }
  return res.json();
};

const pctClass = (p) => (p >= 65 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : p >= 50 ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-red-100 text-red-800 border-red-200');

function StudentRow({ s, year, canOverride, onDecide }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const decide = async (d) => {
    setBusy(true);
    try { await onDecide(s.student_id, s.decision === d ? null : d); } finally { setBusy(false); }
  };
  return (
    <li className="p-4" data-testid={`perf-row-${s.student_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen(!open)} className="flex-1 min-w-[200px] text-left flex items-center gap-2" data-testid={`perf-toggle-${s.student_id}`}>
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          <div>
            <p className="font-medium">{s.name}</p>
            <p className="text-xs text-muted-foreground">{s.subjects.length} subject{s.subjects.length === 1 ? '' : 's'} · {s.subjects_passed} passed · {s.subjects_failed} below 50%</p>
          </div>
        </button>
        <div className="text-center min-w-[80px]">
          {s.average === null ? <span className="text-xs text-muted-foreground">No marks</span> : (
            <><p className={`text-lg font-bold ${s.average >= 50 ? 'text-emerald-700' : 'text-red-700'}`} data-testid={`perf-avg-${s.student_id}`}>{s.average}%</p><p className="text-xs text-muted-foreground">Grade {s.overall_grade}</p></>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={s.decision === 'pass' ? 'default' : 'outline'} disabled={busy} onClick={() => decide('pass')}
            className={s.decision === 'pass' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'} data-testid={`perf-pass-${s.student_id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Pass → Year {year + 1}
          </Button>
          <Button size="sm" variant={s.decision === 'repeat' ? 'default' : 'outline'} disabled={busy} onClick={() => decide('repeat')}
            className={s.decision === 'repeat' ? 'bg-red-600 hover:bg-red-700' : 'text-red-700 border-red-300 hover:bg-red-50'} data-testid={`perf-repeat-${s.student_id}`}>
            <RotateCcw className="h-4 w-4 mr-1" /> Repeat Year {year}
          </Button>
        </div>
      </div>
      {s.decision && <p className="text-xs text-muted-foreground mt-1 ml-6" data-testid={`perf-decided-${s.student_id}`}>Marked {s.decision === 'pass' ? 'Pass' : 'Repeat'} by {s.decided_by_name || 'staff'}{s.decided_at ? ` on ${new Date(s.decided_at).toLocaleDateString()}` : ''}{!canOverride ? '' : ' — you can change this'}</p>}
      {open && (
        <div className="mt-3 ml-6 flex flex-wrap gap-2" data-testid={`perf-subjects-${s.student_id}`}>
          {s.subjects.length === 0 && <span className="text-sm text-muted-foreground">No marks recorded for this term yet.</span>}
          {s.subjects.map((sub) => (
            <span key={sub.class_id} className={`text-xs border rounded-full px-3 py-1 ${pctClass(sub.percentage)}`}>{sub.subject}: <b>{sub.percentage}%</b> ({sub.grade})</span>
          ))}
        </div>
      )}
    </li>
  );
}

export default function PerformanceAnalysisPage() {
  const [opts, setOpts] = useState(null);
  const [year, setYear] = useState('');
  const [term, setTerm] = useState('All Terms');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api('/options').then((o) => { setOpts(o); setYear((y) => y || String(o.years[0] || '')); if (o.terms?.length) setTerm(o.terms[o.terms.length - 1]); }).catch((e) => toast.error(e.message));
  }, []);

  const load = useCallback(() => {
    if (!year) return;
    setLoading(true);
    api(`/students?year=${year}&term=${encodeURIComponent(term)}`).then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [year, term]);
  useEffect(() => { load(); }, [load]);

  const onDecide = async (student_id, decision) => {
    try {
      const r = await api('/decision', { method: 'POST', body: JSON.stringify({ student_id, year: Number(year), term, decision }) });
      setData((d) => ({ ...d, students: d.students.map((s) => (s.student_id === student_id ? { ...s, decision, decided_by_name: decision ? r.decided_by_name : null, decided_at: decision ? new Date().toISOString() : null } : s)) }));
      toast.success(decision ? `Marked ${decision === 'pass' ? 'Pass' : 'Repeat'}` : 'Decision cleared');
    } catch (e) { toast.error(e.message); }
  };

  const students = (data?.students || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()))
    .filter((s) => (filter === 'all' ? true : filter === 'undecided' ? !s.decision : s.decision === filter));
  const summary = data ? { ...data.summary, pass: data.students.filter((s) => s.decision === 'pass').length, repeat: data.students.filter((s) => s.decision === 'repeat').length } : null;
  if (summary) summary.undecided = summary.students - summary.pass - summary.repeat;

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-6 w-6 text-primary" /> Student Performance Analysis</h1>
        <p className="text-muted-foreground text-sm mt-1">Each student's results across all subjects. Mark each student <b>Pass</b> (moves to the next year) or <b>Repeat</b> (stays in the same year). Parents see the decision on the published report card.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={year} onChange={(e) => setYear(e.target.value)} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="perf-year-select">
          {(opts?.years || []).map((y) => <option key={y} value={y}>Year {y}</option>)}
        </select>
        <select value={term} onChange={(e) => setTerm(e.target.value)} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="perf-term-select">
          <option value="All Terms">All terms</option>
          {(opts?.terms || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student..." className="pl-9" data-testid="perf-search" />
        </div>
        {data && (data.published ? <Badge className="bg-emerald-600" data-testid="perf-published-badge">Report cards published</Badge> : <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="perf-unpublished-badge">Report cards not yet published</Badge>)}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="perf-summary">
          {[['all', 'Students', summary.students, ''], ['pass', 'Pass', summary.pass, 'text-emerald-700'], ['repeat', 'Repeat', summary.repeat, 'text-red-700'], ['undecided', 'Not yet decided', summary.undecided, 'text-amber-700']].map(([k, label, n, cls]) => (
            <button key={k} onClick={() => setFilter(k)} className={`text-left rounded-lg border p-4 bg-card hover:bg-muted/50 ${filter === k ? 'ring-2 ring-primary' : ''}`} data-testid={`perf-filter-${k}`}>
              <p className="text-xs text-muted-foreground">{label}</p><p className={`text-2xl font-bold ${cls}`}>{n}</p>
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Year {year} students {loading ? <Loader2 className="inline h-4 w-4 animate-spin ml-1" /> : <span className="font-normal text-muted-foreground">({students.length})</span>}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y" data-testid="perf-student-list">
            {students.map((s) => <StudentRow key={s.student_id} s={s} year={Number(year)} canOverride={data?.can_override} onDecide={onDecide} />)}
            {!loading && students.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><XCircle className="h-4 w-4" /> No students found.</li>}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
